import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Button,
  Divider,
  Chip,
  Avatar,
  Card,
  CardContent,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import { useTranslation } from '../hooks/useTranslation';
import {
  Business as BusinessIcon,
  Settings as SettingsIcon,
  ReceiptLong as ReceiptLongIcon,
  Payment as PaymentIcon,
  LocalShipping as ShippingIcon,
  Description as DescriptionIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Language as LanguageIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  TrendingUp as TrendingUpIcon,
  AccountCircle as AccountCircleIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useThemeContext } from '../context/ThemeContext';
import settingsService from '../services/settingsService';
import BackupTab from '../components/BackupTab';
import { Alert, Snackbar, CircularProgress, Backdrop } from '@mui/material';

function Settings() {
  const { mode, toggleTheme } = useThemeContext();
  const [activeTab, setActiveTab] = useState(0);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState({
    general: {
      autoSave: true,
      darkMode: false,
      printRemark: true,
      defaultB2Cprintsize: 'thermal',
      autoGenerate: false,
      customerAutoCode: false,
      itemAutoCode: false,
      supplierAutoCode: false
    },
    product: {
      defaultUnit: 'pcs',
      defaultTaxType: 'standard',
      enableBarcodeScanning: true,
      trackInventory: true
    },
    sales: {
      invoicePrefix: 'INV-',
      draftPrefix: 'DRF-',
      documentDiscountEnabled: true,
      discountMode: 'tax_exclusive'
    },
    purchase: {
      purchaseReturnPrefix: 'PR-',
      purchasePrefix: 'PO-',
      requirePurchaseApproval: true
    },
    template: {
      poTemplate: 'standard',
      salesInvoiceTemplate: 'standard',
      purchaseInvoiceTemplate: 'standard',
      creditNoteTemplate: 'standard',
      debitNoteTemplate: 'standard',
      purchaseReturnTemplate: 'standard',
      proformaInvoiceTemplate: 'standard',
      deliveryNoteTemplate: 'standard',
    },
    backup: {
      enabled: false,
      frequency: 'daily',
      timeOfDay: '02:00',
      dayOfWeek: 0,
      dayOfMonth: 1,
    }
  });

  const setBackupForm = (updater) => {
    setSettings(prev => ({
      ...prev,
      backup: typeof updater === 'function' ? updater(prev.backup || {}) : updater
    }));
  };

  const updateSettingsState = (apiData) => {
    if (!apiData) return;
    const discountMode = apiData.sales?.discountMode || 'tax_exclusive';
    setSettings(prev => ({
      ...prev,
      ...apiData,
      sales: {
        ...(apiData.sales || {}),
        discountMode
      },
      backup: {
        ...(prev.backup || {}),
        ...(apiData.backup || {})
      }
    }));
  };

  // Load settings on component mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);

      // Fetch both main settings and backup settings concurrently
      const [response, backupResponse] = await Promise.all([
        settingsService.getSettings(),
        settingsService.getCategory('backup').catch(() => ({ data: null }))
      ]);

      if (response.success && response.data) {
        // Merge backup data into the response data before updating state
        if (backupResponse && backupResponse.data) {
          response.data.backup = backupResponse.data;
        }

        updateSettingsState(response.data);
        // Sync dark mode with theme context
        if (response.data.general?.darkMode !== (mode === 'dark')) {
          toggleTheme();
        }
      }
    } catch (err) {
      console.error('Error loading settings:', err);
      setError(t('Settings.Load_Error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category, field, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  const handleToggle = (category, field) => async (event) => {
    const newValue = event.target.checked;
    if (category === 'general' && field === 'darkMode') {
      toggleTheme();
    }

    handleCategoryChange(category, field, newValue);

    // Immediately trigger API call for specific general settings
    if (category === 'general' && ['printRemark', 'autoGenerate', 'customerAutoCode', 'itemAutoCode', 'supplierAutoCode'].includes(field)) {
      handleImmediateSave(category, field, newValue);
    }
  };

  const handleImmediateSave = async (category, field, value) => {
    const prevValue = settings[category]?.[field];
    try {
      setSaving(true);
      const updatedSettings = {
        ...settings,
        [category]: {
          ...settings[category],
          [field]: value,
          ...(field === 'printRemark' && { showPrintRemark: value })
        }
      };
      const response = await settingsService.updateSettings(updatedSettings);
      if (response.success) {
        if (response.data) {
          setSettings(response.data);
        }
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        handleCategoryChange(category, field, prevValue);
      }
    } catch (err) {
      console.error(`Error saving ${field}:`, err);
      setError(err.message || t('Settings.Save_Error'));
      handleCategoryChange(category, field, prevValue);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const response = await settingsService.updateSettings(settings);

      // Also save backup settings
      if (settings.backup) {
        let backupDataToSave = { ...settings.backup };
        if (backupDataToSave.frequency === 'monthly' && (backupDataToSave.dayOfMonth === '' || backupDataToSave.dayOfMonth == null)) {
          backupDataToSave.dayOfMonth = 1;
          setSettings(prev => ({
            ...prev,
            backup: backupDataToSave
          }));
        }
        await settingsService.updateBackupSettings(backupDataToSave);
      }

      if (response.success) {
        if (response.data) {
          setSettings(response.data);
        }
        setSaveSuccess(true);
        setTimeout(() => {
          setSaveSuccess(false);
        }, 3000);
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.message || t('Settings.Save_Error_Retry'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Backdrop open={true} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={saveSuccess}
        autoHideDuration={3000}
        onClose={() => setSaveSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSaveSuccess(false)} severity="success" sx={{ width: '100%' }}>
          {t('Settings.Save_Success')}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Box>
          <Typography variant="h5" component="h1" fontWeight="bold">
             {t('Settings.Title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
           {t('Settings.Subtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          size="small"
          sx={{
            borderRadius: 0,
            textTransform: 'none',
            py: 0.5,
            px: 1,
          }}
        >
          {saving ? t('Settings.Saving') : t('Settings.Save_Changes')}
        </Button>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(event, newValue) => setActiveTab(newValue)}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 0.5,
          minHeight: 36,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500,
            minWidth: 120,
            py: 0.5,
            minHeight: 36,
          }
        }}
      >
       <Tab label={t('Settings.General')} />
<Tab label={t('Settings.Product')} />
<Tab label={t('Settings.Sales')} />
<Tab label={t('Settings.Purchase')} />
<Tab label={t('Settings.Template')} />
<Tab label={t('Settings.Backup')} />
      </Tabs>

      {/* Content Area */}
      <Box>
        {activeTab !== 5 ? (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 0.5,
              borderRadius: 0,
              boxShadow: 'none',
              border: '1px solid rgba(0, 0, 0, 0.08)',
            }}
          >
            {/* General Tab */}
            {activeTab === 0 && (
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.general?.autoSave || false}
                          onChange={handleToggle('general', 'autoSave')}
                        />
                      }
                      label={
                        <Box>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                            {t('Settings.Auto_Save')}
                          </Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            {t('Settings.Auto_Save_Desc')}
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={mode === 'dark'}
                          onChange={handleToggle('general', 'darkMode')}
                        />
                      }
                      label={
                        <Box>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                            {t('Settings.Dark_Mode')}
                          </Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            {t('Settings.Dark_Mode_Desc')}
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.general?.printRemark ?? true}
                          onChange={handleToggle('general', 'printRemark')}
                        />
                      }
                      label={
                        <Box>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                            {t('Settings.Show_Print_Remark')}
                          </Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            {t('Settings.Show_Print_Remark_Desc')}
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>

                  {/* <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.general?.autoGenerate || false}
                      onChange={handleToggle('general', 'autoGenerate')}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                        {t('Settings.Auto_Generate')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        {t('Settings.Auto_Generate_Desc')}
                      </Typography>
                    </Box>
                  }
                />
              </Grid> */}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.general?.customerAutoCode || false}
                          onChange={handleToggle('general', 'customerAutoCode')}
                        />
                      }
                      label={
                        <Box>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                            {t('Settings.Customer_Auto_Code')}
                          </Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            {t('Settings.Customer_Auto_Code_Desc')}
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.general?.itemAutoCode || false}
                          onChange={handleToggle('general', 'itemAutoCode')}
                        />
                      }
                      label={
                        <Box>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                            {t('Settings.Item_Auto_Code')}
                          </Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            {t('Settings.Item_Auto_Code_Desc')}
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.general?.supplierAutoCode || false}
                          onChange={handleToggle('general', 'supplierAutoCode')}
                        />
                      }
                      label={
                        <Box>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                            {t('Settings.Supplier_Auto_Code')}
                          </Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            {t('Settings.Supplier_Auto_Code_Desc')}
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl size="small" sx={{ width: '300px' }}>
                      <InputLabel>{t('Settings.Default_B2C_Print_Size')}</InputLabel>
                      <Select
                        value={settings.general?.defaultB2Cprintsize || 'thermal'}
                        onChange={(e) => {
                          handleCategoryChange('general', 'defaultB2Cprintsize', e.target.value);
                          handleImmediateSave('general', 'defaultB2Cprintsize', e.target.value);
                        }}
                        label={t('Settings.Default_B2C_Print_Size')}
                        sx={{ borderRadius: 0 }}
                      >
                        <MenuItem value="thermal">{t('Settings.Thermal')}</MenuItem>
                        <MenuItem value="a4">{t('Settings.A4_PDF')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Product Tab */}
            {activeTab === 1 && (
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('Settings.Default_Unit')}</InputLabel>
                      <Select
                        value={settings.product?.defaultUnit || 'pcs'}
                        onChange={(e) => handleCategoryChange('product', 'defaultUnit', e.target.value)}
                        label={t('Settings.Default_Unit')}
                        sx={{ borderRadius: 0 }}
                      >
                        <MenuItem value="pcs">{t('Settings.Pieces')}</MenuItem>
                        <MenuItem value="kg">{t('Settings.Kilogram')}</MenuItem>
                        <MenuItem value="ltr">{t('Settings.Liter')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('Settings.Default_Tax_Type')}</InputLabel>
                      <Select
                        value={settings.product?.defaultTaxType || 'standard'}
                        onChange={(e) => handleCategoryChange('product', 'defaultTaxType', e.target.value)}
                        label={t('Settings.Default_Tax_Type')}
                        sx={{ borderRadius: 0 }}
                      >
                        <MenuItem value="standard">{t('Settings.Standard_Tax')}</MenuItem>
                        <MenuItem value="exempt">{t('Settings.Exempt')}</MenuItem>
                        <MenuItem value="zero">{t('Settings.Zero_Rated')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.product?.enableBarcodeScanning || false}
                          onChange={handleToggle('product', 'enableBarcodeScanning')}
                        />
                      }
                      label={
                        <Box>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                            {t('Settings.Enable_Barcode_Scanning')}
                          </Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            {t('Settings.Barcode_Scanning_Desc')}
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.product?.trackInventory || false}
                          onChange={handleToggle('product', 'trackInventory')}
                        />
                      }
                      label={
                        <Box>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                            {t('Settings.Track_Inventory')}
                          </Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            {t('Settings.Track_Inventory_Desc')}
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Sales Tab */}
            {activeTab === 2 && (
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('Settings.Invoice_Prefix')}
                      value={settings.sales?.invoicePrefix || ''}
                      onChange={(e) => handleCategoryChange('sales', 'invoicePrefix', e.target.value)}
                      placeholder={t('Settings.Invoice_Prefix_Placeholder')}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('Settings.Draft_Prefix')}
                      value={settings.sales?.draftPrefix || ''}
                      onChange={(e) => handleCategoryChange('sales', 'draftPrefix', e.target.value)}
                      placeholder={t('Settings.Draft_Prefix_Placeholder')}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="discount-mode-label">{t('Settings.Discount_Mode')}</InputLabel>
                      <Select
                        labelId="discount-mode-label"
                        id="discount-mode-select"
                        value={settings.sales?.discountMode || 'tax_exclusive'}
                        onChange={(e) => {
                          handleCategoryChange('sales', 'discountMode', e.target.value);
                        }}
                        label={t('Settings.Discount_Mode')}
                        sx={{ borderRadius: 0 }}
                      >
                        <MenuItem value="tax_exclusive">{t('Settings.Tax_Exclusive')}</MenuItem>
                        <MenuItem value="tax_inclusive">{t('Settings.Tax_Inclusive')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.sales?.documentDiscountEnabled || false}
                          onChange={handleToggle('sales', 'documentDiscountEnabled')}
                        />
                      }
                      label={
                        <Box>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                            {t('Settings.Document_Discount_Enabled')}
                          </Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            {t('Settings.Document_Discount_Desc')}
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Purchase Tab */}
            {activeTab === 3 && (
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('Settings.Purchase_Return_Prefix')}
                      value={settings.purchase?.purchaseReturnPrefix || 'PR-'}
                      onChange={(e) => handleCategoryChange('purchase', 'purchaseReturnPrefix', e.target.value)}
                      placeholder={t('Settings.Purchase_Return_Prefix_Placeholder')}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('Settings.Purchase_Order_Prefix')}
                      value={settings.purchase?.purchasePrefix || 'PO-'}
                      onChange={(e) => handleCategoryChange('purchase', 'purchasePrefix', e.target.value)}
                      placeholder={t('Settings.Purchase_Order_Prefix_Placeholder')}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.purchase?.requirePurchaseApproval || false}
                          onChange={handleToggle('purchase', 'requirePurchaseApproval')}
                        />
                      }
                      label={
                        <Box>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                            {t('Settings.Require_Purchase_Approval')}
                          </Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            {t('Settings.Purchase_Approval_Desc')}
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Template Tab */}
            {activeTab === 4 && (
              <Box>
                <Grid container spacing={2}>
                  {[
                    { id: 'poTemplate', labelKey: 'PO_Template' },
                    { id: 'salesInvoiceTemplate', labelKey: 'Sales_Invoice_Template' },
                    { id: 'purchaseInvoiceTemplate', labelKey: 'Purchase_Invoice_Template' },
                    { id: 'creditNoteTemplate', labelKey: 'Credit_Note_Template' },
                    { id: 'debitNoteTemplate', labelKey: 'Debit_Note_Template' },
                    { id: 'purchaseReturnTemplate', labelKey: 'Purchase_Return_Template' },
                    { id: 'proformaInvoiceTemplate', labelKey: 'Proforma_Invoice_Template' },
                    { id: 'deliveryNoteTemplate', labelKey: 'Delivery_Note_Template' },
                  ].map((template) => {
                    const label = t(`Settings.${template.labelKey}`);
                    return (
                      <Grid item xs={12} sm={6} key={template.id}>
                        <FormControl fullWidth size="small">
                          <InputLabel>{label}</InputLabel>
                          <Select
                            value={settings.template?.[template.id] || 'standard'}
                            onChange={(e) => handleCategoryChange('template', template.id, e.target.value)}
                            label={label}
                            sx={{ borderRadius: 0 }}
                          >
                            <MenuItem value="standard">{t('Settings.Standard_Template')}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}


          </Paper>
        ) : (
          /* Backup Tab */
          <Box sx={{ mt: 1 }}>
            <BackupTab
              backupForm={settings.backup}
              setBackupForm={setBackupForm}
            />
          </Box>
        )}
      </Box>
    </Container>
  );
}

export default Settings; 
